import React, { useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import AddIcon from "@mui/icons-material/Add";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import { useTranslation } from "react-i18next";

import ExperienceEntry from "./ExperienceEntry";
import type { ExperienceRecord } from "./ExperienceEntry";
import ExperienceEntryForm from "./ExperienceEntryForm";
import tokens from "@/theme/tokens";
import {
  toggleEditExperience,
  toggleAddExperience,
} from "@/store/profile/profileActions";
import { contentPanelSx, panelHeadSx } from "../styles";

interface ProfileExperienceProps {
  isUsersProfile: boolean;
}

/** Most recent first; entries still in progress sort above finished ones. */
const byRecency = (a: ExperienceRecord, b: ExperienceRecord) => {
  const sameEnd =
    new Date(b.end_date ?? "").getMonth() ===
    new Date(a.end_date ?? "").getMonth() &&
    new Date(b.end_date ?? "").getFullYear() ===
    new Date(a.end_date ?? "").getFullYear();

  if ((a.present && b.present) || sameEnd) {
    return (
      new Date(b.start_date ?? "").getTime() -
      new Date(a.start_date ?? "").getTime()
    );
  }
  if (b.present) return 1;
  if (a.present) return -1;
  return (
    new Date(b.end_date ?? "").getTime() - new Date(a.end_date ?? "").getTime()
  );
};

const ProfileExperience = ({ isUsersProfile }: ProfileExperienceProps) => {
  const { t } = useTranslation(["translation.Profile"]);
  const dispatch = useDispatch();

  const experience: ExperienceRecord[] = useSelector((state: any) =>
    isUsersProfile
      ? state.profile.experience.data
      : state.browse.browseProfile.getIn(["experience", "data"]).toJS()
  );
  const isEditingExperience = useSelector((state: any) =>
    isUsersProfile ? state.profile.isEditingExperience : null
  );
  const isAddingExperience = useSelector((state: any) =>
    isUsersProfile ? Boolean(state.profile.isAddingExperience) : false
  );

  const editExperience = useCallback(
    (id: number) => dispatch(toggleEditExperience(id)),
    [dispatch]
  );

  const renderEntries = () => {
    if (experience.length === 0) {
      return (
        <Typography
          variant="body1"
          sx={{ color: "grey.800", fontStyle: "italic" }}
        >
          {isUsersProfile ? t("ProfilePhrase5") : t("NoExperience")}
        </Typography>
      );
    }

    // sort() mutates, and the redux slice is handed to us directly
    return [...experience].sort(byRecency).map((entry) =>
      isEditingExperience === entry.id ? (
        <ExperienceEntryForm key={entry.id} entry={entry} formId={entry.id} formType="edit" />
      ) : (
        <ExperienceEntry
          key={entry.id}
          experience={entry}
          editExperience={editExperience}
          isUsersProfile={isUsersProfile}
        />
      )
    );
  };

  return (
    <Box sx={contentPanelSx}>
      <Box sx={panelHeadSx}>
        <Typography variant="h2">{t("Experience")}</Typography>
        {isUsersProfile && (
          <IconButton
            aria-label="add experience"
            onClick={() => dispatch(toggleAddExperience())}
            size="small"
            sx={{ color: tokens.color.accentTeal }}
          >
            <AddIcon fontSize="small" />
          </IconButton>
        )}
      </Box>
      {isUsersProfile && isAddingExperience && <ExperienceEntryForm />}
      {renderEntries()}
    </Box>
  );
};

export default ProfileExperience;
